import * as React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ExternalLink, FileText, Home, Users, Building2, Info, Stethoscope, Loader2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useCitiesFromDatabase } from '@/hooks/useCitiesFromDatabase';
import { useCitySpecialtiesFromDatabase, useCitySpecialtiesCount } from '@/hooks/useCitySpecialtiesFromDatabase';
import { citySpecialties } from '@/data/citySpecialties';
import NumberedPagination from '@/components/sitemap/NumberedPagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Sitemap = () => {
  const { t, getLocalizedPath } = useLanguage();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [specialtiesPage, setSpecialtiesPage] = React.useState(1);
  const itemsPerPage = 150;

  const { data: cityData, isLoading: citiesLoading } = useCitiesFromDatabase({ 
    page: currentPage, 
    pageSize: itemsPerPage 
  });

  const { data: specialtiesCount } = useCitySpecialtiesCount();
  const { data: specialtiesData, isLoading: specialtiesLoading } = useCitySpecialtiesFromDatabase({
    page: specialtiesPage,
    pageSize: itemsPerPage
  });

  const cities = cityData?.cities || [];
  const totalPages = cityData?.totalPages || 0;
  const totalCitiesCount = cityData?.totalCount || 0;

  const specialtyLinks = specialtiesData?.links || [];
  const specialtyTotalPages = specialtiesData?.totalPages || 0;
  const specialtyTotalCount = specialtiesData?.totalCount || 0;

  const sitemapSections = [
    {
      title: "Main Pages",
      icon: <Home className="h-5 w-5" />,
      links: [
        { name: "Home", path: "/" },
        { name: "Find Therapist", path: "/find" },
        { name: "How It Works", path: "/how-it-works" },
        { name: "Pricing", path: "/pricing" },
        { name: "Specialties", path: "/specialties" },
        { name: "Directory", path: "/directory" }
      ]
    },
    {
      title: "About & Information",
      icon: <Info className="h-5 w-5" />,
      links: [
        { name: "About Us", path: "/about" },
        { name: "Dr. José Luis Marín", path: "/jose-luis-marin" },
        { name: "Contact", path: "/contact" },
        { name: "Blog", path: "/blog" }
      ]
    },
    {
      title: "For Clients",
      icon: <Users className="h-5 w-5" />,
      links: [
        { name: "Online Therapy", path: "/online-therapy" },
        { name: "Cities", path: "/cities" },
        { name: "Countries", path: "/countries" },
        { name: "Login", path: "/login" }
      ]
    },
    {
      title: "For Therapists",
      icon: <Building2 className="h-5 w-5" />,
      links: [
        { name: "For Therapists", path: "/for-therapists" },
        { name: "Resources", path: "/resources" },
        { name: "Therapist FAQ", path: "/therapist-faq" }
      ]
    },
    {
      title: "Legal & Support",
      icon: <FileText className="h-5 w-5" />,
      links: [
        { name: "Privacy Policy", path: "/privacy" },
        { name: "Terms of Service", path: "/terms" },
        { name: "Cookie Policy", path: "/cookie-policy" }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Sitemap | Global Therapy Hub</title>
        <meta name="description" content="Browse our complete sitemap with 32,000+ cities and 850,000+ specialty pages to find certified therapists, psychologists, and mental health professionals worldwide." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl font-bold mb-4 text-center">
              Sitemap
            </h1>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Navigate through all pages, {totalCitiesCount.toLocaleString()} cities, and {specialtyTotalCount.toLocaleString()} specialty pages in our global mental health directory.
            </p>

      <div className="space-y-6">
        <div className="text-center py-8 bg-card rounded-lg border">
          <h2 className="text-2xl font-semibold mb-4">Website Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div>
              <div className="text-4xl font-bold text-primary">{sitemapSections.length}</div>
              <div className="text-sm text-muted-foreground">Main Sections</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary">{totalCitiesCount.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Cities</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary">{citySpecialties.length}</div>
              <div className="text-sm text-muted-foreground">Specialties</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary">{specialtyTotalCount.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Specialty Pages</div>
            </div>
          </div>
        </div>

        {sitemapSections.map((section, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {section.icon}
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    to={getLocalizedPath(link.path)}
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {link.name}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* City Specialties Section - REAL PAGINATION */}
        <div className="mt-12 pt-8 border-t">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Stethoscope className="h-6 w-6 text-primary" />
                City Specialties
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {specialtyTotalCount.toLocaleString()} specialty pages ({totalCitiesCount.toLocaleString()} cities × {citySpecialties.length} specialties)
              </p>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Specialty Sub-Landings</span>
                <span className="text-sm font-normal text-muted-foreground">
                  Page {specialtiesPage.toLocaleString()} of {specialtyTotalPages.toLocaleString()}
                </span>
              </CardTitle>
              <CardDescription>
                Showing {specialtyLinks.length} of {specialtyTotalCount.toLocaleString()} total specialty pages
              </CardDescription>
            </CardHeader>
            <CardContent>
              {specialtiesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {specialtyLinks.map((link, index) => (
                    <Link
                      key={`${link.citySlug}-${link.specialtySlug}-${index}`}
                      to={link.url}
                      className="text-sm text-primary hover:underline hover:text-primary/80 transition-colors py-1 px-2 rounded hover:bg-accent"
                    >
                      {link.specialtyName} in {link.cityName}
                      {link.cityStateProvince && `, ${link.cityStateProvince}`}
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
            <CardContent className="pt-0">
              <NumberedPagination
                currentPage={specialtiesPage}
                totalPages={specialtyTotalPages}
                onPageChange={(page) => {
                  setSpecialtiesPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                label="Page"
                section="specialties"
              />
            </CardContent>
          </Card>
        </div>

        {/* City Directory Section */}
        <div className="mt-12 pt-8 border-t">
          <h2 className="text-2xl font-semibold mb-6">All Cities ({totalCitiesCount.toLocaleString()})</h2>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>City Directory</span>
                <span className="text-sm font-normal text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
              </CardTitle>
              <CardDescription>
                Showing {cities.length} of {totalCitiesCount.toLocaleString()} cities
              </CardDescription>
            </CardHeader>
            <CardContent>
              {citiesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {cities.map((city) => (
                    <Link
                      key={city.id}
                      to={`/city/${city.slug}`}
                      className="text-primary hover:underline"
                    >
                      {city.name}{city.state_province ? `, ${city.state_province}` : ''}, {city.country}
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
            <CardContent className="pt-0">
              <NumberedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                label="Page"
                section="cities"
              />
            </CardContent>
          </Card>
        </div>

        {/* External Resources */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              External Resources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a 
                href="https://formacionpsicoterapia.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Master in Brief Psychotherapy Training
              </a>
              <a 
                href="https://linkedin.com/company/psychotherapist-help" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                LinkedIn
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Contact Support */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground mb-4">
            Can't find what you're looking for?
          </p>
          <Button asChild>
            <Link to="/contact">Contact Support</Link>
          </Button>
        </div>
      </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sitemap;
